import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pause, Play, UserCog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/layout/PageHeader';
import { ScrollTable } from '@/components/layout/ScrollTable';
import { ResponsiveGrid } from '@/components/layout/ResponsiveGrid';
import { apiFetch, ApiError } from '@/lib/api';
import { setImpersonationToken } from '@/lib/auth';
import { useT } from '@/lib/i18n';

interface TenantDoc {
  id: string;
  name: string;
  status: string;
  contact_email?: string;
  created_at: string;
  updated_at: string;
}

interface DetailResp {
  tenant: TenantDoc;
  counts: Record<string, number>;
}

interface UserRow {
  name: string;
  email: string;
  displayName?: string;
  roles: string[];
  deactivated?: boolean;
}

export function TenantDetail() {
  const t = useT();
  const { id } = useParams();
  const nav = useNavigate();
  const [detail, setDetail] = useState<DetailResp | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const d = await apiFetch<DetailResp>(`/tenants/${id}`);
      setDetail(d);
      try {
        const u = await apiFetch<{ users: UserRow[] }>(`/tenants/${id}/users`);
        setUsers(u.users ?? []);
      } catch {
        setUsers([]);
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'failed');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleStatus = async () => {
    if (!detail) return;
    setBusy(true);
    const next = detail.tenant.status === 'active' ? 'suspended' : 'active';
    try {
      await apiFetch(`/tenants/${detail.tenant.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: next }),
      });
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'failed');
    } finally {
      setBusy(false);
    }
  };

  const impersonate = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      const r = await apiFetch<{
        accessToken: string;
        tenant: { id: string; name: string };
      }>(`/tenants/${detail.tenant.id}/impersonate`, { method: 'POST' });
      await setImpersonationToken(r.accessToken);
      nav('/overview');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        {t('auth.loading')}
      </div>
    );
  }
  if (err || !detail) {
    return (
      <div className="p-6 text-sm text-destructive">
        {err ?? t('superadmin.tenantDetail.notFound')}
      </div>
    );
  }

  const { tenant, counts } = detail;
  return (
    <div className="space-y-4">
      <PageHeader
        title={tenant.name}
        subtitle={tenant.id}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={toggleStatus}
              disabled={busy}
              variant={tenant.status === 'active' ? 'destructive' : 'default'}
            >
              {tenant.status === 'active' ? (
                <>
                  <Pause className="mr-1 h-4 w-4" />
                  {t('superadmin.tenantDetail.suspend')}
                </>
              ) : (
                <>
                  <Play className="mr-1 h-4 w-4" />
                  {t('superadmin.tenantDetail.unsuspend')}
                </>
              )}
            </Button>
            <Button onClick={impersonate} disabled={busy}>
              <UserCog className="mr-1 h-4 w-4" />
              {t('superadmin.tenantDetail.impersonate')}
            </Button>
          </div>
        }
      />

      <ResponsiveGrid cols={3} gap={4}>
        <StatCard label={t('superadmin.tenantDetail.status')}>
          <Badge variant={tenant.status === 'active' ? 'default' : 'secondary'}>
            {tenant.status}
          </Badge>
        </StatCard>
        <StatCard label={t('superadmin.tenantDetail.contactEmail')}>
          <span className="text-sm">{tenant.contact_email || '—'}</span>
        </StatCard>
        <StatCard label={t('superadmin.tenantDetail.created')}>
          <span className="text-sm">{tenant.created_at?.slice(0, 10)}</span>
        </StatCard>
        <StatCard label={t('superadmin.tenantDetail.customers')}>
          <span className="text-2xl font-bold">{counts.customer ?? 0}</span>
        </StatCard>
        <StatCard label={t('superadmin.tenantDetail.extinguishers')}>
          <span className="text-2xl font-bold">{counts.extinguisher ?? 0}</span>
        </StatCard>
        <StatCard label={t('superadmin.tenantDetail.visits')}>
          <span className="text-2xl font-bold">{counts.visit ?? 0}</span>
        </StatCard>
      </ResponsiveGrid>

      <Card>
        <CardHeader>
          <CardTitle>{t('superadmin.tenantDetail.users')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {users.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              {t('superadmin.tenantDetail.noUsers')}
            </div>
          ) : (
            <ScrollTable>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('superadmin.tenantDetail.userEmail')}
                    </TableHead>
                    <TableHead>
                      {t('superadmin.tenantDetail.userRoles')}
                    </TableHead>
                    <TableHead className="hidden md:table-cell">
                      {t('superadmin.tenantDetail.userStatus')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.email}>
                      <TableCell className="font-medium">{u.email}</TableCell>
                      <TableCell className="text-xs">
                        {u.roles
                          .filter(r => !r.startsWith('tenant:'))
                          .join(', ')}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {u.deactivated ? (
                          <Badge variant="secondary">
                            {t('superadmin.tenantDetail.deactivated')}
                          </Badge>
                        ) : (
                          <Badge variant="default">
                            {t('superadmin.tenantDetail.active')}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollTable>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
          {label}
        </div>
        <div>{children}</div>
      </CardContent>
    </Card>
  );
}
